import { useCallback } from "react";

import { useIsAutoblessedModelsEnabled } from "@features/mmm/components/media-plan/hooks/use-is-autoblessed-models-enabled";
import { ARCHIVE_CYCLE } from "@gql/mmm-archive-cycle";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type ArchiveCycle,
  type ArchiveCycleVariables,
} from "@north-beam/nb-client-side";
import { type MMMClientCycle } from "@north-beam/nb-common";
import { useClientCycles } from "@pages/mmm/hooks/use-client-cycles";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { useMediaPlanData } from "../components/media-plan/hooks/use-media-plan";

export const useDeleteCycle = () => {
  const areAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();
  const [clientCycles, setClientCycles] = useClientCycles();
  const { selectedCycle, setSelectedCycle } = useSelectedCycle();
  const { setMediaPlanList } = useMediaPlanData();
  const [_deleteCycle, { loading: isDeleteCycleLoading }] =
    useNorthbeamMutation<ArchiveCycle, ArchiveCycleVariables>(ARCHIVE_CYCLE, {
      doNotRaiseErrorOnFailure: true,
      update: (cache, { data }) => {
        if (data && data.archiveCycle) {
          const cycleId = data.archiveCycle.id;

          // Remove the mmmCycleData from the cache
          cache.evict({
            id: cache.identify({
              __typename: "mmmCycleData",
              id: cycleId,
            }),
          });

          cache.evict({
            id: cache.identify({
              __typename: "basicMediaPlan",
              id: cycleId,
            }),
          });

          // Remove the mmmMediaPlan from the cache
          cache.evict({
            id: cache.identify({
              __typename: "mmmMediaPlan",
              id: cycleId,
            }),
          });

          // Garbage collect any dangling references
          cache.gc();
        }
      },
    });

  const deleteCycle = useCallback(
    async (cycleId: string) => {
      if (selectedCycle.id === cycleId) {
        const nextCycle = clientCycles.find((cycle) => cycle.id !== cycleId);
        if (clientCycles.length <= 1 || !nextCycle) {
          return false;
        }
        // TODO Fix typing here. MMMClientCycleBase vs MMMClientCycle
        setSelectedCycle(nextCycle as unknown as MMMClientCycle);
      }
      setClientCycles((prevCycles) =>
        prevCycles.filter((cycle) => cycle.id !== cycleId),
      );
      if (!areAutoblessedModelsEnabled) {
        setMediaPlanList((prevMediaPlanList) =>
          prevMediaPlanList.filter((mediaPlan) => mediaPlan.id !== cycleId),
        );
      }
      await _deleteCycle({ variables: { cycleId } });
      return true;
    },
    [
      _deleteCycle,
      selectedCycle,
      clientCycles,
      setSelectedCycle,
      setClientCycles,
      setMediaPlanList,
      areAutoblessedModelsEnabled,
    ],
  );

  return { deleteCycle, isDeleteCycleLoading };
};
